// SPDX-License-Identifier: AGPL-3.0-or-later

import {
	decodeVoiceEngineV2CodecGossip,
	type VoiceEngineV2CodecGossipMessage,
	type VoiceEngineV2LocalStreamSource,
	type VoiceEngineV2VideoCodec,
	type VoiceEngineV2WatchedStream,
} from '@konvo/voice_engine_v2';

export interface VoiceEngineV2CodecGossipController {
	reportStreamViewer(
		source: VoiceEngineV2LocalStreamSource,
		viewerIdentity: string,
		watching: boolean,
		supportedVideoCodecs: Array<VoiceEngineV2VideoCodec>,
	): void;
	reportRemoteVideoCodecCapability(identity: string, supportedVideoCodecs: Array<VoiceEngineV2VideoCodec>): void;
}

export interface VoiceEngineV2WatchedStreamGossip {
	destinationIdentity: string;
	message: VoiceEngineV2CodecGossipMessage;
}

export interface VoiceEngineV2WatchedStreamGossipResult {
	messages: Array<VoiceEngineV2WatchedStreamGossip>;
	next: Map<string, {identity: string; source: VoiceEngineV2LocalStreamSource}>;
}

function watchedStreamGossipKey(identity: string, source: VoiceEngineV2LocalStreamSource): string {
	return `${source} ${identity}`;
}

export function ingestVoiceEngineV2CodecGossip(
	controller: VoiceEngineV2CodecGossipController,
	senderIdentity: string,
	payload: ArrayBuffer | ArrayBufferView | string,
): boolean {
	if (senderIdentity.length === 0) return false;
	const message = decodeVoiceEngineV2CodecGossip(payload);
	if (message === null) return false;
	if (message.kind === 'codec.capability') {
		controller.reportRemoteVideoCodecCapability(senderIdentity, message.supportedVideoCodecs);
		return true;
	}
	controller.reportStreamViewer(message.source, senderIdentity, message.watching, message.supportedVideoCodecs);
	return true;
}

export function computeVoiceEngineV2WatchedStreamGossip(
	previous: Map<string, {identity: string; source: VoiceEngineV2LocalStreamSource}>,
	watchedStreams: Record<string, VoiceEngineV2WatchedStream>,
	localSupportedVideoCodecs: Array<VoiceEngineV2VideoCodec>,
): VoiceEngineV2WatchedStreamGossipResult {
	const next = new Map<string, {identity: string; source: VoiceEngineV2LocalStreamSource}>();
	for (const stream of Object.values(watchedStreams)) {
		if (stream.source !== 'camera' && stream.source !== 'screen') continue;
		next.set(watchedStreamGossipKey(stream.participantIdentity, stream.source), {
			identity: stream.participantIdentity,
			source: stream.source,
		});
	}
	const messages: Array<VoiceEngineV2WatchedStreamGossip> = [];
	for (const [key, entry] of next) {
		if (previous.has(key)) continue;
		messages.push({
			destinationIdentity: entry.identity,
			message: {
				kind: 'codec.viewing',
				source: entry.source,
				watching: true,
				supportedVideoCodecs: localSupportedVideoCodecs,
			},
		});
	}
	for (const [key, entry] of previous) {
		if (next.has(key)) continue;
		messages.push({
			destinationIdentity: entry.identity,
			message: {
				kind: 'codec.viewing',
				source: entry.source,
				watching: false,
				supportedVideoCodecs: localSupportedVideoCodecs,
			},
		});
	}
	return {messages, next};
}
